/**
 * --------------------------------------------------------------------------
 * Splide Carousel Component Documentation & API
 * --------------------------------------------------------------------------
 *
 * BASIC EXAMPLE USAGE:
 *
 * <div class="splide-carousel"
 *      data-itemssm="1"
 *      data-itemsmd="2"
 *      data-itemslg="3"
 *      data-gap="16px"
 *      data-loop="true">
 *     <div>Slide 1</div>
 *     <div>Slide 2</div>
 *     <div>Slide 3</div>
 * </div>
 *
 * --------------------------------------------------------------------------
 * FULL AVAILABLE ATTRIBUTES REFERENCE:
 * --------------------------------------------------------------------------
 *
 * LAYOUT & RESPONSIVE:
 * - data-itemssm="1"              : Items per page at base (< 768px)
 * - data-itemsmd="2"              : Items per page at >= 768px
 * - data-itemslg="3"              : Items per page at >= 992px
 * - data-itemsxl="4"              : Items per page at >= 1024px
 * - data-itemsxxl="5"             : Items per page at >= 1600px
 * - data-by="page"                : Slide step count at base / default (integer, float or 'page')
 * - data-bysm="1.5"               : Slide step count at base (< 768px)
 * - data-bymd="2.5"               : Slide step count at >= 768px
 * - data-bylg="3.5"               : Slide step count at >= 992px
 * - data-byxl="4"                 : Slide step count at >= 1024px
 * - data-byxxl="5"                : Slide step count at >= 1600px
 * - data-startindex="0"           : Zero-based initial active slide index
 * - data-gap="16px"               : Spacing between slides (px, rem, %, etc.)
 * - data-padding="24px"           : Side padding revealing adjacent slides
 * - data-paddingsm="24px"         : Side padding at base (< 768px) (string or JSON e.g. '{"left":"10%","right":"0"}')
 * - data-paddingmd="24px"         : Side padding at >= 768px (string or JSON)
 * - data-paddinglg="24px"         : Side padding at >= 992px (string or JSON)
 * - data-paddingxl="24px"         : Side padding at >= 1024px (string or JSON)
 * - data-direction="ltr"          : Movement direction: 'ltr' | 'rtl' | 'ttb'
 * - data-height="400px"           : Fixed container height (required for 'ttb')
 * - data-autowidth="true"         : Adjust slide size based on child content width
 * - data-autoheight="true"        : Dynamically adapt height to active slide
 * - data-fixedwidth="240px"       : Enforce fixed slide width
 * - data-fixedheight="240px"      : Enforce fixed slide height
 * - data-heightratio="0.5"        : Set slide height as ratio relative to width
 *
 * MOTION & BEHAVIOR:
 * - data-type="slide"             : Animation mode: 'slide' | 'loop' | 'fade'
 * - data-loop="true"              : Shorthand alias to set type="loop"
 * - data-speed="300"              : Transition duration in milliseconds
 * - data-easing="cubic-bezier()"  : Custom CSS easing function
 * - data-rewind="true"            : Jump back to first slide upon reaching the end
 * - data-rewindspeed="600"        : Rewind animation duration in ms
 * - data-flickpower="600"         : Swipe/flick velocity sensitivity
 * - data-drag="true"              : Drag mode: true | false | "free"
 * - data-center="true"            : Focus active slide in center ('center' | false)
 * - data-trimspace="true"         : Prevent empty edge space when loop is disabled
 * - data-updateonmove="true"      : Update slide active state classes during move
 *
 * AUTOPLAY:
 * - data-autoplay="true"          : Enable auto-advancing slides
 * - data-timeout="3000"           : Interval between slide transitions in ms
 * - data-pauseonhover="true"      : Pause autoplay timer when mouse enters
 * - data-pauseonfocus="true"      : Pause autoplay timer when focus moves inside
 * - data-resetprogress="true"     : Reset progress timer upon manual navigation
 *
 * CONTROLS & NAVIGATION:
 * - data-controls="true"          : Render built-in prev/next arrows (default: true)
 * - data-controls-container="#id" : Selector for controls wrapper (wires up existing buttons
 *                                   [data-control="prev"]/[data-control="next"] or auto-generates them;
 *                                   automatically hidden when all slides fit without overflow)
 * - data-dots-container="#id"     : Selector for custom pagination dots wrapper
 * - data-controlsposition="bottom": Appends `is-controls-bottom` class for CSS placement
 * - data-nav="true"               : Render pagination dots (default: true)
 * - data-navposition="bottom"     : Appends `is-nav-bottom` class for CSS placement
 * - data-paginationkeyboard="true": Allow arrow key navigation over pagination dots
 * - data-customdots="true"        : Executes window.runCustomDots(splide) on mount
 *
 * THUMBNAIL SYNCING:
 * - data-thumbnails="#my-thumbs"  : CSS selector of thumbnail navigation instance
 * - data-isnavigation="true"      : Marks this carousel instance as a clickable thumbnail
 *
 * INTERACTIVITY & ACCESSIBILITY:
 * - data-arrows="true"            : Enables global arrow keyboard navigation
 * - data-wheel="true"             : Navigate slides using mouse/trackpad wheel
 * - data-live="true"              : Attach aria-live regions for screen readers
 *
 * MEDIA & ADVANCED:
 * - data-cover="true"             : Set slide images (`<img>`) to background object-fit cover
 * - data-lazyload="nearby"        : Image lazy loading: false | "nearby" | "sequential"
 * - data-clones="8"               : Force specific clone count in loop mode
 * - data-start="768"              : Conditional init (only active when viewport < 768px)
 */

import Splide from '@splidejs/splide';

window.TC = window.TC || {};

TC.Carousel = {
	instances: new Map(),

	/**
	 * Default carousel options (Splide-native)
	 */
	defaults: {
		type: 'slide',
		perPage: 1,
		speed: 300,
		autoplay: false,
		interval: 3000,
		arrows: true,
		pagination: true,
		rewind: false,
		gap: '0px',
		drag: true,
		keyboard: 'global',
		focus: false,
		start: 0,
		mediaQuery: 'min', // 'min' maps breakpoints to min-width (mobile-first)
		omitEnd: true,
	},

	/**
	 * Parse data attributes into Splide options
	 * @param el
	 */
	parseDataAttributes(el) {
		const data = el.dataset;
		const options = {};

		// 1. Layout & Responsive Breakpoints
		const itemsSm = parseFloat(data.itemssm);
		const itemsMd = parseFloat(data.itemsmd);
		const itemsLg = parseFloat(data.itemslg);
		const itemsXl = parseFloat(data.itemsxl);
		const itemsXxl = parseFloat(data.itemsxxl);

		const parseVal = (val) => {
			if (!val) return null;
			try {
				return JSON.parse(val);
			} catch (e) {
				return val;
			}
		};

		const parseByVal = (val) => {
			if (val === undefined || val === null || val === '') return null;
			if (val === 'page') return 'page';
			const parsed = parseFloat(val);
			if (isNaN(parsed)) return val;
			// perMove in Splide must be an integer count of slides (or 'page')
			return Math.max(1, Math.floor(parsed));
		};

		const bySm = parseByVal(data.bysm || data.by);
		const byMd = parseByVal(data.bymd);
		const byLg = parseByVal(data.bylg || data.byLg);
		const byXl = parseByVal(data.byxl || data.byXl);
		const byXxl = parseByVal(data.byxxl || data.byXxl);

		if (itemsSm) {
			options.perPage = itemsSm;
		}

		if (bySm !== null) {
			options.perMove = bySm;
		}

		const paddingSm = parseVal(data.paddingsm);
		const paddingMd = parseVal(data.paddingmd);
		const paddingLg = parseVal(data.paddinglg || data.paddingLg);
		const paddingXl = parseVal(data.paddingxl || data.paddingXl);

		if (paddingSm) {
			options.padding = paddingSm;
		} else if (data.padding && !paddingMd && !paddingLg) {
			options.padding = parseVal(data.padding);
		}

		const breakpoints = {};
		if (itemsMd || paddingMd || byMd !== null) {
			breakpoints[768] = {};
			if (itemsMd) breakpoints[768].perPage = itemsMd;
			if (paddingMd) breakpoints[768].padding = paddingMd;
			if (byMd !== null) breakpoints[768].perMove = byMd;
		}
		if (itemsLg || paddingLg || byLg !== null) {
			breakpoints[992] = {};
			if (itemsLg) breakpoints[992].perPage = itemsLg;
			if (paddingLg) breakpoints[992].padding = paddingLg;
			if (byLg !== null) breakpoints[992].perMove = byLg;
		}
		if (itemsXl || paddingXl || byXl !== null) {
			breakpoints[1024] = {};
			if (itemsXl) breakpoints[1024].perPage = itemsXl;
			if (paddingXl) breakpoints[1024].padding = paddingXl;
			if (byXl !== null) breakpoints[1024].perMove = byXl;
		}
		if (itemsXxl || byXxl !== null) {
			breakpoints[1600] = {};
			if (itemsXxl) breakpoints[1600].perPage = itemsXxl;
			if (byXxl !== null) breakpoints[1600].perMove = byXxl;
		}

		if (Object.keys(breakpoints).length > 0) {
			options.breakpoints = breakpoints;
		}
		if (data.direction) {
			options.direction = data.direction;
		} // 'ltr' | 'rtl' | 'ttb'
		if (data.height) {
			options.height = data.height;
		}
		if (data.autowidth !== undefined) {
			options.autoWidth = data.autowidth === 'true';
		}
		if (data.autoheight !== undefined) {
			options.autoHeight = data.autoheight === 'true';
		}
		if (data.fixedwidth) {
			options.fixedWidth = data.fixedwidth;
		}
		if (data.fixedheight) {
			options.fixedHeight = data.fixedheight;
		}
		if (data.heightratio) {
			options.heightRatio = parseFloat(data.heightratio);
		}

		// 2. Motion & Transitions
		if (data.type) {
			options.type = data.type;
		} // 'slide' | 'loop' | 'fade'
		if (data.loop !== undefined) {
			options.type = data.loop === 'true' ? 'loop' : 'slide';
		}
		if (data.speed) {
			options.speed = parseInt(data.speed, 10);
		}
		if (data.easing) {
			options.easing = data.easing;
		}
		if (data.rewind !== undefined) {
			options.rewind = data.rewind === 'true';
		}
		if (data.rewindspeed) {
			options.rewindSpeed = parseInt(data.rewindspeed, 10);
		}
		if (data.flickpower) {
			options.flickPower = parseInt(data.flickpower, 10);
		}
		if (data.trimspace !== undefined) {
			options.trimSpace =
				data.trimspace === 'true'
					? true
					: data.trimspace === 'false'
						? false
						: data.trimspace;
		}
		if (data.updateonmove !== undefined) {
			options.updateOnMove = data.updateonmove === 'true';
		}

		// Drag mode
		if (data.drag !== undefined) {
			if (data.drag === 'true') {
				options.drag = true;
			} else if (data.drag === 'false') {
				options.drag = false;
			} else {
				options.drag = data.drag;
			} // e.g. "free"
		}

		// Center / Focus mode
		if (data.center !== undefined) {
			options.focus =
				data.center === 'true'
					? 'center'
					: data.center === 'false'
						? false
						: data.center;
		}

		// 3. Autoplay Settings
		if (data.autoplay !== undefined) {
			options.autoplay = data.autoplay === 'true';
		}
		if (data.timeout) {
			options.interval = parseInt(data.timeout, 10);
		}
		if (data.pauseonhover !== undefined) {
			options.pauseOnHover = data.pauseonhover === 'true';
		}
		if (data.pauseonfocus !== undefined) {
			options.pauseOnFocus = data.pauseonfocus === 'true';
		}
		if (data.resetprogress !== undefined) {
			options.resetProgress = data.resetprogress === 'true';
		}

		// 4. Controls & Positioning
		if (data.controls !== undefined) {
			options.arrows = data.controls !== 'false';
		}

		// Unified Controls Container (Supports data-controls-container, data-controlscontainer, or legacy data-controler)
		if (
			data.controlsContainer ||
			data.controlscontainer ||
			data.controler
		) {
			options._controlsContainerSelector =
				data.controlsContainer ||
				data.controlscontainer ||
				data.controler;
			options.arrows = false; // Disable native inline arrows when container is used
		}
		if (data.controlsposition && data.controlsposition !== 'false') {
			options._controlsPositionClass = `is-controls-${data.controlsposition}`;
		}

		// 5. Pagination & Navigation
		if (data.nav !== undefined) {
			options.pagination = data.nav === 'true';
		}
		if (data.dotsContainer || data.dotscontainer) {
			options._dotsContainerSelector =
				data.dotsContainer || data.dotscontainer;
		}
		if (data.navposition && data.navposition !== 'false') {
			options._navPositionClass = `is-nav-${data.navposition}`;
		}
		if (data.paginationkeyboard !== undefined) {
			options.paginationKeyboard = data.paginationkeyboard === 'true';
		}
		if (data.customdots !== undefined) {
			options._customDots = data.customdots === 'true';
		}

		// 6. Thumbnail Navigation Syncing
		if (data.thumbnails) {
			options._thumbnailsSelector = data.thumbnails;
		}
		if (data.isnavigation !== undefined) {
			options.isNavigation = data.isnavigation === 'true';
		}

		// 7. Keyboard, Mouse Wheel & Accessibility
		if (data.arrows !== undefined) {
			options.keyboard = data.arrows === 'true' ? 'global' : false;
		}
		if (data.wheel !== undefined) {
			options.wheel = data.wheel === 'true';
		}
		if (data.live !== undefined) {
			options.live = data.live === 'true';
		}

		// 8. Gallery, Lazy Loading & Clones
		if (data.cover !== undefined) {
			options.cover = data.cover === 'true';
		}
		if (data.lazyload !== undefined) {
			if (data.lazyload === 'true') {
				options.lazyLoad = true;
			} else if (data.lazyload === 'false') {
				options.lazyLoad = false;
			} else {
				options.lazyLoad = data.lazyload;
			}
		}
		if (data.clones) {
			options.clones = parseInt(data.clones, 10);
		}

		// 9. Screen Breakpoint Filter
		if (data.start) {
			options._screenBreak = parseInt(data.start, 10);
		}

		return options;
	},

	/**
	 * Ensures Splide markup structure without wrapping non-slide children
	 * @param el
	 */
	ensureSplideStructure(el) {
		el.classList.add('splide');

		if (el.querySelector(':scope > .splide__track')) {
			return;
		}

		const track = document.createElement('div');
		track.className = 'splide__track';
		const list = document.createElement('div');
		list.className = 'splide__list';

		const children = Array.from(el.children).filter(
			(child) =>
				!child.hasAttribute('data-splide-ignore') &&
				!child.tagName.match(/^(H1|H2|H3|H4|H5|H6)$/i)
		);

		children.forEach((child) => {
			if (child.classList.contains('splide__slide')) {
				list.appendChild(child);
			} else {
				const slide = document.createElement('div');
				slide.className = 'splide__slide';
				slide.appendChild(child);
				list.appendChild(slide);
			}
		});

		track.appendChild(list);
		el.appendChild(track);
	},

	/**
	 * Render fallback external control buttons if container is empty
	 * @param containerEl
	 * @param splide
	 */
	buildExternalControls(containerEl, splide) {
		const prevBtn = document.createElement('button');
		const nextBtn = document.createElement('button');
		prevBtn.type = 'button';
		nextBtn.type = 'button';
		prevBtn.className = 'carousel-control carousel-control--prev';
		nextBtn.className = 'carousel-control carousel-control--next';
		prevBtn.ariaLabel = 'Previous slide';
		nextBtn.ariaLabel = 'Next slide';
		prevBtn.innerHTML = '&lsaquo;';
		nextBtn.innerHTML = '&rsaquo;';

		const onPrev = () => splide.go('<');
		const onNext = () => splide.go('>');

		const updateState = () => {
			const Components = splide.Components;
			if (Components && Components.Arrows) {
				const isLoop =
					splide.options.type === 'loop' || splide.options.rewind;
				prevBtn.disabled = !isLoop && splide.index === 0;
				nextBtn.disabled =
					!isLoop && splide.index === Components.Controller.getEnd();
			}
		};

		prevBtn.addEventListener('click', onPrev);
		nextBtn.addEventListener('click', onNext);
		splide.on('mounted moved updated', updateState);

		containerEl.appendChild(prevBtn);
		containerEl.appendChild(nextBtn);

		return () => {
			prevBtn.removeEventListener('click', onPrev);
			nextBtn.removeEventListener('click', onNext);
			prevBtn.remove();
			nextBtn.remove();
		};
	},

	/**
	 * Initialize a single carousel
	 * @param container
	 * @param options
	 */
	init(container, options = {}) {
		const el =
			typeof container === 'string'
				? document.querySelector(container)
				: container;

		if (!el) {
			return null;
		}
		if (this.instances.has(el)) {
			return this.instances.get(el).splide;
		}

		// GUARD 1: Prevent section wrapper blocks from being converted into sliders
		if (el.querySelector('.splide-carousel')) {
			return null;
		}

		const isExplicitCarousel =
			el.classList.contains('splide-carousel') ||
			el.classList.contains('splide') ||
			el.querySelector(':scope > .splide__track');

		const dataOptions = this.parseDataAttributes(el);

		// GUARD 2: Ignore plain container classes without slider markup or data attributes
		if (!isExplicitCarousel && Object.keys(dataOptions).length === 0) {
			return null;
		}

		const config = {
			...this.defaults,
			...dataOptions,
			...options,
		};

		// Screen breakpoint check: only init if viewport is smaller than data-start
		if (config._screenBreak && window.innerWidth >= config._screenBreak) {
			return null;
		}

		// Pull internal flags
		const screenBreak = config._screenBreak;
		const customDots = config._customDots;
		const controlsContainerSelector = config._controlsContainerSelector;
		const dotsContainerSelector = config._dotsContainerSelector;
		const controlsPositionClass = config._controlsPositionClass;
		const navPositionClass = config._navPositionClass;
		const thumbnailsSelector = config._thumbnailsSelector;

		delete config._screenBreak;
		delete config._customDots;
		delete config._controlsContainerSelector;
		delete config._dotsContainerSelector;
		delete config._controlsPositionClass;
		delete config._navPositionClass;
		delete config._thumbnailsSelector;

		// Apply helper classes to wrapper
		if (controlsPositionClass) {
			el.classList.add(controlsPositionClass);
		}
		if (navPositionClass) {
			el.classList.add(navPositionClass);
		}

		// Ensure structural compliance
		this.ensureSplideStructure(el);

		// Instantiate
		const splide = new Splide(el, config);

		let controlsCleanup = null;
		let thumbSplide = null;

		// 1. Thumbnail Sync MUST run before .mount()
		if (thumbnailsSelector) {
			const thumbEl = document.querySelector(thumbnailsSelector);
			if (thumbEl) {
				thumbSplide = this.instances.has(thumbEl)
					? this.instances.get(thumbEl).splide
					: this.init(thumbEl);

				if (thumbSplide) {
					splide.sync(thumbSplide);
				}
			}
		}

		// 2. Custom Dots Container
		if (dotsContainerSelector) {
			const dotsEl = document.querySelector(dotsContainerSelector);
			if (dotsEl) {
				splide.on('pagination:mounted', (data) => {
					dotsEl.appendChild(data.list);
				});
			}
		}

		// 3. Controls Container (Smart handler: pre-authored buttons OR auto-generated)
		if (controlsContainerSelector) {
			const controlsEl = document.querySelector(
				controlsContainerSelector
			);

			if (controlsEl) {
				let prevBtn = controlsEl.querySelector(
					'[data-control="prev"], .carousel-control--prev, .prev'
				);
				let nextBtn = controlsEl.querySelector(
					'[data-control="next"], .carousel-control--next, .next'
				);

				if (!prevBtn || !nextBtn) {
					const buttons = controlsEl.querySelectorAll('button');
					if (buttons.length >= 2) {
						prevBtn = prevBtn || buttons[0];
						nextBtn = nextBtn || buttons[1];
					}
				}

				if (prevBtn && nextBtn) {
					const onPrev = () => splide.go('<');
					const onNext = () => splide.go('>');

					prevBtn.addEventListener('click', onPrev);
					nextBtn.addEventListener('click', onNext);

					const updateState = () => {
						const Controller = splide.Components?.Controller;
						const Layout = splide.Components?.Layout;
						const Slides = splide.Components?.Slides;
						if (!Controller) {
							return;
						}

						// Splide v4: Layout.isOverflow() is the correct API
						// Fallback: if slides end index is 0, nothing to scroll
						const isOverflow = Layout?.isOverflow?.() ?? Controller.getEnd() > 0;
						controlsEl.style.display = isOverflow ? '' : 'none';

						const isLoop =
							splide.options.type === 'loop' ||
							splide.options.rewind;

						prevBtn.disabled = !isLoop && Controller.getPrev() < 0;
						nextBtn.disabled = !isLoop && Controller.getNext() < 0;
					};

					splide.on('mounted moved updated resized overflow', updateState);

					controlsCleanup = () => {
						prevBtn.removeEventListener('click', onPrev);
						nextBtn.removeEventListener('click', onNext);
					};
				} else {
					controlsCleanup = this.buildExternalControls(
						controlsEl,
						splide
					);
				}
			}
		}

		// 4. Overflow & Simple Class Handler (adds .simple class when all items fit without overflow)
		const updateOverflowState = (label) => {
			const Controller = splide.Components?.Controller;
			const Layout = splide.Components?.Layout;
			if (!Controller) return;

			const isOverflow = Layout?.isOverflow?.() ?? Controller.getEnd() > 0;
			el.classList.toggle('simple', !isOverflow);
		};

		splide.on('mounted moved updated resized overflow', updateOverflowState);

		// Mount Splide
		splide.mount();

		// Trigger check on next frame in case dimensions settle post-mount
		requestAnimationFrame(() => updateOverflowState('raf'));

		// Re-check after a short delay (catches staging servers where CSS/fonts load slower)
		setTimeout(() => updateOverflowState('delay-300'), 300);
		setTimeout(() => updateOverflowState('delay-800'), 800);

		// Re-check once all page resources (fonts, images) have fully loaded
		if (document.readyState !== 'complete') {
			window.addEventListener('load', () => {
				splide.refresh();
				requestAnimationFrame(() => updateOverflowState('window-load'));
			}, { once: true });
		} else {
			// Page already loaded — do an immediate extra refresh
			splide.refresh();
			requestAnimationFrame(() => updateOverflowState('already-loaded'));
		}

		// ResizeObserver: catches late layout shifts (e.g. webfonts causing reflow)
		if (typeof ResizeObserver !== 'undefined') {
			const ro = new ResizeObserver(() => {
				updateOverflowState('resize-observer');
			});
			ro.observe(el);
			// Store observer for cleanup on destroy
			el._splideResizeObserver = ro;
		}

		// Run custom dots callback if requested
		if (customDots && typeof window.runCustomDots === 'function') {
			window.runCustomDots(splide);
		}

		// Store instance reference
		this.instances.set(el, {
			splide,
			screenBreak,
			thumbSplide: () => thumbSplide,
			controlsCleanup: () => controlsCleanup && controlsCleanup(),
		});

		return splide;
	},

	/**
	 * Initialize all carousels (.splide-carousel or .carousel)
	 */
	initAll() {
		const carousels = document.querySelectorAll(
			'.splide-carousel, .carousel'
		);
		carousels.forEach((el) => {
			if (!this.instances.has(el)) {
				this.init(el);
			}
		});
	},

	destroy(el) {
		const instance = this.instances.get(el);
		if (instance && instance.splide) {
			if (instance.controlsCleanup) {
				instance.controlsCleanup();
			}
			// Disconnect ResizeObserver if attached
			if (el._splideResizeObserver) {
				el._splideResizeObserver.disconnect();
				delete el._splideResizeObserver;
			}
			instance.splide.destroy(true);
			this.instances.delete(el);
		}
	},

	destroyAll() {
		this.instances.forEach((_, el) => this.destroy(el));
	},

	refresh() {
		this.destroyAll();
		this.initAll();
	},
};

// Auto-initialize on DOM ready or immediately if already loaded
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => {
		TC.Carousel.initAll();
	});
} else {
	TC.Carousel.initAll();
}

// Re-evaluate sliders if `data-start` (screenBreak) is used
let resizeTimeout;
window.addEventListener('resize', () => {
	clearTimeout(resizeTimeout);
	resizeTimeout = setTimeout(() => {
		let hasBreakpoints = false;
		TC.Carousel.instances.forEach((instance) => {
			if (instance.screenBreak) {
				hasBreakpoints = true;
			}
		});
		if (hasBreakpoints) {
			TC.Carousel.refresh();
		}
	}, 250);
});

export default TC.Carousel;
